import React, { lazy, Suspense } from 'react';
import { motion } from 'framer-motion';

/**
 * 代码分割和懒加载工具
 */

// 通用加载组件
export const LoadingSpinner = ({ size = 'default', className = '' }) => {
  const sizeClasses = {
    small: 'w-4 h-4',
    default: 'w-8 h-8',
    large: 'w-12 h-12'
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <motion.div
        className={`border-2 border-primary-200 border-t-primary-500 rounded-full ${sizeClasses[size]}`}
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
    </div>
  );
};

// 骨架屏组件
export const SkeletonLoader = ({ 
  lines = 3, 
  className = '',
  showAvatar = false,
  showImage = false 
}) => {
  return (
    <div className={`animate-pulse ${className}`}>
      {showAvatar && (
        <div className="flex items-center space-x-4 mb-4">
          <div className="w-10 h-10 bg-dark-200 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-dark-200 rounded w-1/4 mb-2" />
            <div className="h-3 bg-dark-200 rounded w-1/3" />
          </div>
        </div>
      )}
      
      {showImage && (
        <div className="w-full h-48 bg-dark-200 rounded-lg mb-4" />
      )}
      
      <div className="space-y-3">
        {Array.from({ length: lines }).map((_, index) => (
          <div key={index} className="space-y-2">
            <div className="h-4 bg-dark-200 rounded w-full" />
            {index === lines - 1 && (
              <div className="h-4 bg-dark-200 rounded w-2/3" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// 卡片骨架屏
export const CardSkeleton = ({ className = '' }) => (
  <div className={`bg-white rounded-lg p-6 shadow-card ${className}`}>
    <SkeletonLoader lines={3} showImage />
  </div>
);

// 列表骨架屏
export const ListSkeleton = ({ items = 3, className = '' }) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: items }).map((_, index) => (
      <SkeletonLoader key={index} lines={2} showAvatar />
    ))}
  </div>
);

// 高级加载组件
export const AdvancedLoader = ({ 
  type = 'spinner',
  message = '加载中...',
  className = '' 
}) => {
  const loaderTypes = {
    spinner: (
      <motion.div
        className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
      />
    ),
    dots: (
      <div className="flex space-x-1">
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className="w-2 h-2 bg-primary-500 rounded-full"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.7, 1, 0.7]
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: index * 0.2
            }}
          />
        ))}
      </div>
    ),
    pulse: (
      <motion.div
        className="w-8 h-8 bg-primary-500 rounded-full"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.7, 1, 0.7]
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity
        }}
      />
    ),
    wave: (
      <div className="flex space-x-1">
        {[0, 1, 2, 3, 4].map((index) => (
          <motion.div
            key={index}
            className="w-1 h-8 bg-primary-500 rounded-full"
            animate={{
              scaleY: [1, 2, 1]
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: index * 0.1
            }}
          />
        ))}
      </div>
    )
  };

  return (
    <motion.div
      className={`flex flex-col items-center justify-center p-8 ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {loaderTypes[type]}
      {message && (
        <motion.p
          className="mt-4 text-dark-600 text-sm"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          {message}
        </motion.p>
      )}
    </motion.div>
  );
};

// 错误边界组件
export const ErrorBoundary = ({ 
  children, 
  fallback,
  onError 
}) => {
  return (
    <ErrorBoundaryClass fallback={fallback} onError={onError}>
      {children}
    </ErrorBoundaryClass>
  );
};

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('组件加载错误:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-dark-900 mb-2">组件加载失败</h3>
          <p className="text-dark-600 mb-4">抱歉，此组件暂时无法加载</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            刷新页面
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// 懒加载包装器
export const LazyWrapper = ({ 
  children, 
  fallback,
  errorFallback,
  loadingType = 'spinner',
  loadingMessage = '加载中...'
}) => {
  const defaultFallback = fallback || (
    <AdvancedLoader type={loadingType} message={loadingMessage} />
  );

  return (
    <ErrorBoundary fallback={errorFallback}>
      <Suspense fallback={defaultFallback}>
        {children}
      </Suspense>
    </ErrorBoundary>
  );
};

// 创建懒加载组件的工具函数
export const createLazyComponent = (
  importFunc,
  fallbackOptions = {}
) => {
  const LazyComponent = lazy(importFunc);
  
  return (props) => (
    <LazyWrapper {...fallbackOptions}>
      <LazyComponent {...props} />
    </LazyWrapper>
  );
};

// 预加载工具
export const preloadComponent = (importFunc) => {
  const componentImport = importFunc();
  return componentImport;
};

// 批量预加载
export const preloadComponents = (importFuncs) => {
  return Promise.all(importFuncs.map(importFunc => importFunc()));
};

// 路由级别的懒加载
export const createLazyRoute = (importFunc, options = {}) => {
  const {
    fallback = <AdvancedLoader type="wave" message="页面加载中..." />,
    errorFallback,
    preload = false
  } = options;

  const LazyComponent = lazy(importFunc);

  if (preload) {
    // 预加载组件
    importFunc();
  }

  return (props) => (
    <LazyWrapper fallback={fallback} errorFallback={errorFallback}>
      <LazyComponent {...props} />
    </LazyWrapper>
  );
};

// 条件懒加载
export const ConditionalLazy = ({ 
  condition, 
  lazyComponent, 
  fallbackComponent,
  loadingComponent 
}) => {
  if (!condition) {
    return fallbackComponent || null;
  }

  return (
    <Suspense fallback={loadingComponent || <LoadingSpinner />}>
      {lazyComponent}
    </Suspense>
  );
};

// 智能预加载策略
export class IntelligentPreloader {
  constructor() {
    this.preloadedComponents = new Set();
    this.preloadQueue = [];
    this.isPreloading = false;
  }

  // 添加到预加载队列
  addToQueue(importFunc, priority = 'normal') {
    this.preloadQueue.push({ importFunc, priority });
    this.processQueue();
  }

  // 处理预加载队列
  async processQueue() {
    if (this.isPreloading || this.preloadQueue.length === 0) return;

    this.isPreloading = true;

    // 按优先级排序
    this.preloadQueue.sort((a, b) => {
      const priorityOrder = { high: 3, normal: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });

    while (this.preloadQueue.length > 0) {
      const { importFunc } = this.preloadQueue.shift();

      try {
        await this.preloadWithDelay(importFunc);
      } catch (error) {
        console.warn('Component preload failed:', error);
      }
    }

    this.isPreloading = false;
  }

  // 带延迟的预加载
  async preloadWithDelay(importFunc, delay = 100) {
    return new Promise((resolve) => {
      setTimeout(async () => {
        try {
          await importFunc();
          resolve();
        } catch (error) {
          console.warn('Preload error:', error);
          resolve();
        }
      }, delay);
    });
  }

  // 基于用户行为的智能预加载
  preloadBasedOnUserBehavior() {
    this.setupHoverPreload();
    this.setupScrollPreload();
    this.setupIdlePreload();
  }

  // 鼠标悬停预加载
  setupHoverPreload() {
    const navLinks = document.querySelectorAll('nav a[href]');

    navLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        const href = link.getAttribute('href');
        this.preloadRouteComponent(href);
      });
    });
  }

  // 滚动预加载
  setupScrollPreload() {
    let scrollTimeout;

    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const scrollPercentage = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;

        if (scrollPercentage > 50) {
          this.preloadNextPageComponents();
        }
      }, 150);
    });
  }

  // 空闲时间预加载
  setupIdlePreload() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.preloadLowPriorityComponents();
      });
    } else {
      setTimeout(() => {
        this.preloadLowPriorityComponents();
      }, 2000);
    }
  }

  // 预加载路由组件
  preloadRouteComponent(href) {
    const routeComponentMap = {
      '/': () => import('../pages/HomePage'),
      '/product': () => import('../pages/ProductPage'),
      '/solutions': () => import('../pages/SolutionsPage'),
      '/cases': () => import('../pages/CasesPage'),
      '/pricing': () => import('../pages/PricingPage'),
      '/resources': () => import('../pages/ResourcesPage')
    };

    const importFunc = routeComponentMap[href];
    if (importFunc && !this.preloadedComponents.has(href)) {
      this.preloadedComponents.add(href);
      this.addToQueue(importFunc, 'high');
    }
  }

  // 预加载下一页组件
  preloadNextPageComponents() {
    const currentPath = window.location.pathname;
    const nextPageMap = {
      '/': '/product',
      '/product': '/solutions',
      '/solutions': '/cases',
      '/cases': '/pricing',
      '/pricing': '/resources'
    };

    const nextPath = nextPageMap[currentPath];
    if (nextPath) {
      this.preloadRouteComponent(nextPath);
    }
  }

  // 预加载低优先级组件
  preloadLowPriorityComponents() {
    const lowPriorityComponents = [
      () => import('../components/Footer'),
      () => import('../components/IndustryCases'),
      () => import('../components/ProductFeatures')
    ];

    lowPriorityComponents.forEach(importFunc => {
      this.addToQueue(importFunc, 'low');
    });
  }
}

// 创建全局预加载器实例
export const globalPreloader = new IntelligentPreloader();

// 初始化智能预加载
export const initializeIntelligentPreload = () => {
  if (document.readyState === 'complete') {
    globalPreloader.preloadBasedOnUserBehavior();
  } else {
    window.addEventListener('load', () => {
      globalPreloader.preloadBasedOnUserBehavior();
    });
  }
};

export default {
  LoadingSpinner,
  SkeletonLoader,
  CardSkeleton,
  ListSkeleton,
  AdvancedLoader,
  ErrorBoundary,
  LazyWrapper,
  createLazyComponent,
  preloadComponent,
  preloadComponents,
  createLazyRoute,
  ConditionalLazy,
  IntelligentPreloader,
  globalPreloader,
  initializeIntelligentPreload
};
